import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classNames from 'classnames';
import _ from 'lodash';

import './BetResult.scss';
import { RootState, Dispatch, select } from '../../../configs/store';
import useDeviceScroll from '../../../hooks/useDeviceScroll';
import Icon from '../../Icon/Icon';
import Button from '../../Button/Button';
import BetLine from './BetLine/BetLine';
import BetCombinations from '../../Account/AccountHistory/BetCombinations/BetCombinations';
import { formatAmountToFixed } from 'src/utils/utils';

export default function BetResult() {
  const [showCombinations, setShowCombinations] = useState(false);
  const [innerHeight, setInnerHeight] = useState(0);
  const [closeAll, setCloseAll] = useState(true);

  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const permutationsRef = useRef(null);

  const { betResult, bets, stakes } = useSelector((state: RootState) => state.betslip);
  const totalCombinations = useSelector(select.betslip.totalCombinations);
  const dispatch = useDispatch<Dispatch>();
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);

  const scrolled = useDeviceScroll();

  const SLIP = {
    1: 'single',
    2: 'accumulator',
    3: 'system'
  };

  let maxHeight = '100%';
  if (wrapperRef) {
    const maxSize = (innerHeight - wrapperRef.current?.getBoundingClientRect().y - 185);

    if (maxSize > 105) {
      maxHeight = maxSize + 'px';
    }
  }

  const reuseBetslip = async (e) => {
    e.preventDefault();

    await dispatch.betslip.clearBetResult();
    dispatch.betslip.applySlipState();
  };

  const clearBetslip =  (e) => {
    e.preventDefault();

    dispatch.betslip.clearBetResult();
  };

  const onScrollSizeChanges = () => {
    if (window.innerHeight !== innerHeight) {
      setInnerHeight(window.innerHeight);
    }
  };

  useEffect(() => {
    const resizeObs = new ResizeObserver(onScrollSizeChanges);

    if (wrapperRef.current) {
      resizeObs.observe(wrapperRef.current);
    }

    return () => {
      resizeObs.unobserve(wrapperRef.current);
    };
  }, [wrapperRef.current]);

  useEffect(() => {
    window.addEventListener('resize', onScrollSizeChanges);

    return () => {
      window.removeEventListener('resize', onScrollSizeChanges);
    };
  }, []);

  useEffect(() => {
    onScrollSizeChanges();

    if (showCombinations === true && permutationsRef.current) {
      setTimeout(() => {
        permutationsRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [showCombinations]);

  useEffect(onScrollSizeChanges, [scrolled]);

  const toggleCombinations = (e) => {
    e.preventDefault();
    setShowCombinations(!showCombinations);
  };

  return (
    <div className="bet-result" ref={ wrapperRef }>
      <div className="bet-result__header">
        <Icon icon="check" />
        <div>
          <div className="bet-result__header-title">{ t('betResult.betPlaced') }</div>
        </div>

        <Icon icon="close" className="bet-result__close" onClick={ () => dispatch.betslip.clearBetResult() } />
      </div>
      <div className="bet-result__bet-header" onClick={ () => setCloseAll(!closeAll) }>
        <div className="bet-result__bet-number">
          <span>{ t('betResult.nr') } { betResult?.code }</span>
          <span className="bet-result__bet-header--type">&nbsp; &nbsp;{ _.capitalize(SLIP[betResult?.betType]) }</span>
        </div>
        <div className="bet-result__datetime">
          { betResult ? format(utcToZonedTime((parseInt(betResult?.registerDate) * 1000), selectedTimezone), 'dd/MM/yyyy HH:mm') : ''  }
        </div>
      </div>

      <div style={ { maxHeight, overflowY: 'auto', overflowX: 'auto' } }>
        <div className="bet-result__bet-list">
          {
            _.map(bets, (bet, index: number) => {
              return (
                <BetLine
                  key={ bet.id }
                  index={ index }
                  bet={ bet }
                  slip={ SLIP[betResult?.betType] }
                  closeAll={ closeAll }
                />
              );
            })
          }
        </div>
        {
          stakes[0] && (
            <div className="bet-result__combinations">
              <div className="bet-line__combinations-info">
                <div className="bet-line__combinations-info-column">
                  <div>
                    <span className="bet-result__label">{ t('betHistory.totalStake') }:&nbsp;</span>
                    <span className="bet-result__label-value">
                      { formatAmountToFixed(betResult?.totalStake, betResult.divisor) }
                    </span>
                  </div>
                  <div>
                    <span className="bet-result__label">{ t('betHistory.potencialProfit') }:&nbsp;</span>
                    <span className="bet-result__label-value">
                      { formatAmountToFixed(betResult?.maximumProfit, betResult.divisor) }
                    </span>
                  </div>
                  <div>
                    <span className="bet-result__label">{ t('betHistory.potencialReturn') }:&nbsp;</span>
                    <span className="bet-result__label-value">
                      { formatAmountToFixed(betResult?.maximumReturn, betResult.divisor) }
                    </span>
                  </div>
                </div>
                <div className="bet-line__combinations-info-column">
                  <div>
                    <span className="bet-result__label">{ t('betHistory.potentialTax') }:&nbsp;</span>
                    <span className="bet-result__label-value">
                      { formatAmountToFixed(betResult?.totalPotentialTax, betResult.divisor) }
                    </span>
                  </div>
                  <div>
                    <span className="bet-result__label">{ t('betHistory.potencialNetReturn') }:&nbsp;</span>
                    <span className="bet-result__label-value">
                      { formatAmountToFixed(betResult?.potentialNetReturn, betResult.divisor) }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        { /* ANY OF BETS */ }

        <div className="slip-form__combinations">
          <div>
            <span className="slip-form__combinations-title">{ t('betslip.betsLabel') }: { totalCombinations }</span>
          </div>
          <a
            href={ stakes.length > 0 ? '#' : null }
            className="slip-form__combinations-link"
            onClick={ toggleCombinations }
          >
            { showCombinations ?  t('betslip.hide') : t('betslip.show') }
          </a>
        </div>
        {
          <div
            className={
              classNames('slip-form__permutations', {
                'slip-form__permutations--expanded': showCombinations
              })
            }
            ref={ permutationsRef }
          >
            <BetCombinations item={ { stakes: { combinations: stakes } } } betslip={ true } />
          </div>
        }
      </div>

      <div>
        <div className="bet-result__actions">
          <Button text={ t('betResult.reuse') } onClick={ reuseBetslip } />
          <Button text={ t('betResult.done') } onClick={ clearBetslip } />
        </div>
      </div>
    </div>
  );
}
